import { useEffect, useState, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  Timestamp
} from 'firebase/firestore'
import Database from '../../../types/Database'
import { useTestDataStore } from '../../../stores/testDataStore'

type DiveStatus = Database.Dive['status']

const DIVE_STATUS_COLORS: Record<
  DiveStatus,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  open: 'success',
  full: 'info',
  cancelled: 'warning',
  completed: 'default',
  removed: 'error'
}

interface DiveDetail {
  id: string
  siteName: string
  hostId: string
  description: string
  locationDisplay: string
  dateTimeDisplay: string
  status: DiveStatus
  price: number | null
  maxParticipants: number
  type: 'buddy' | 'guided'
  duration: number
  depthTier: string
  moderationReason: string | null
}

interface TransactionRow {
  id: string
  diveId: string
  payerId: string
  hostId: string
  amount: number
  stripeFee: number | null
  platformCommission: number | null
  netOwedToHost: number | null
  payoutStatus: string
  diveName: string
  diveStatus: DiveStatus | null
  participantName: string
  hostName: string
}

const formatAUD = (amount: number | null): string => {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount)
}

const TransactionLedgerTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const [transactions, setTransactions] = useState<TransactionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Date range filter – default to current calendar month
  const now = new Date()
  const defaultStart = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}-01`
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  const defaultEnd = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`

  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)

  // Filters
  const [hostIdFilter, setHostIdFilter] = useState('')
  const [diveIdFilter, setDiveIdFilter] = useState('')
  const [payoutStatusFilter, setPayoutStatusFilter] = useState<string>('')
  const [showIncomplete, setShowIncomplete] = useState(false)

  // Dive detail modal state
  const [diveDetailOpen, setDiveDetailOpen] = useState(false)
  const [diveDetailLoading, setDiveDetailLoading] = useState(false)
  const [selectedDiveDetail, setSelectedDiveDetail] =
    useState<DiveDetail | null>(null)
  const [selectedDiveHostName, setSelectedDiveHostName] = useState('')

  const handleDiveClick = async (tx: TransactionRow) => {
    setDiveDetailOpen(true)
    setDiveDetailLoading(true)
    setSelectedDiveHostName(tx.hostName)

    try {
      const db = getFirestore()
      const diveDoc = await getDoc(doc(db, 'dives', tx.diveId))
      if (diveDoc.exists()) {
        const data = diveDoc.data()
        const location = data.location
        const dateTime = data.dateTime
        setSelectedDiveDetail({
          id: tx.diveId,
          siteName: (data.siteName as string) || tx.diveId,
          hostId: data.hostId as string,
          description: data.description as string,
          locationDisplay: location
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(
                4
              )}`
            : '—',
          dateTimeDisplay: dateTime?.toDate?.()
            ? dateTime.toDate().toLocaleString()
            : '—',
          status: data.status as DiveStatus,
          price: (data.price as number | null) ?? null,
          maxParticipants: data.maxParticipants as number,
          type: data.type as 'buddy' | 'guided',
          duration: data.duration as number,
          depthTier: data.depthTier as string,
          moderationReason: (data.moderationReason as string | null) ?? null
        })
      } else {
        setSelectedDiveDetail(null)
      }
    } catch {
      setSelectedDiveDetail(null)
    } finally {
      setDiveDetailLoading(false)
    }
  }

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const db = getFirestore()
      const transactionsRef = collection(db, 'transactions')

      const constraints: Parameters<typeof query>[1][] = []

      if (startDate) {
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(start)))
      }
      if (endDate) {
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(end)))
      }

      if (hostIdFilter.trim()) {
        constraints.push(where('hostId', '==', hostIdFilter.trim()))
      }
      if (diveIdFilter.trim()) {
        constraints.push(where('diveId', '==', diveIdFilter.trim()))
      }
      if (payoutStatusFilter) {
        constraints.push(
          where(
            'payoutStatus',
            '==',
            payoutStatusFilter as Database.PayoutStatus
          )
        )
      }

      constraints.push(where('testData', '==', showTestData))
      constraints.push(orderBy('createdAt', 'desc'))

      const q = query(transactionsRef, ...constraints)
      const snapshot = await getDocs(q)

      const rawTransactions = snapshot.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          diveId: data.diveId as string,
          payerId: data.payerId as string,
          hostId: data.hostId as string,
          amount: data.amount as number,
          stripeFee: (data.stripeFee as number | null) ?? null,
          platformCommission:
            (data.platformCommission as number | null) ?? null,
          netOwedToHost: (data.netOwedToHost as number | null) ?? null,
          payoutStatus: data.payoutStatus as string
        }
      })

      // Resolve related document names
      const userCache = new Map<string, string>()
      const diveCache = new Map<
        string,
        { name: string; status: DiveStatus | null; detail: DiveDetail | null }
      >()

      const resolveUserName = async (uid: string): Promise<string> => {
        if (userCache.has(uid)) return userCache.get(uid)!
        try {
          const userDoc = await getDoc(doc(db, 'users', uid))
          const name = userDoc.exists()
            ? (userDoc.data().displayName as string) || uid
            : uid
          userCache.set(uid, name)
          return name
        } catch {
          userCache.set(uid, uid)
          return uid
        }
      }

      const resolveDiveName = async (
        diveId: string
      ): Promise<{
        name: string
        status: DiveStatus | null
        detail: DiveDetail | null
      }> => {
        if (diveCache.has(diveId)) return diveCache.get(diveId)!
        try {
          const diveDoc = await getDoc(doc(db, 'dives', diveId))
          if (diveDoc.exists()) {
            const data = diveDoc.data()
            const location = data.location
            const dateTime = data.dateTime
            const detail: DiveDetail = {
              id: diveId,
              siteName: (data.siteName as string) || diveId,
              hostId: data.hostId as string,
              description: data.description as string,
              locationDisplay: location
                ? `${location.latitude.toFixed(
                    4
                  )}, ${location.longitude.toFixed(4)}`
                : '—',
              dateTimeDisplay: dateTime?.toDate?.()
                ? dateTime.toDate().toLocaleString()
                : '—',
              status: data.status as DiveStatus,
              price: (data.price as number | null) ?? null,
              maxParticipants: data.maxParticipants as number,
              type: data.type as 'buddy' | 'guided',
              duration: data.duration as number,
              depthTier: data.depthTier as string,
              moderationReason: (data.moderationReason as string | null) ?? null
            }
            const result = {
              name: detail.siteName,
              status: detail.status,
              detail
            }
            diveCache.set(diveId, result)
            return result
          }
          const result = { name: diveId, status: null, detail: null }
          diveCache.set(diveId, result)
          return result
        } catch {
          const result = { name: diveId, status: null, detail: null }
          diveCache.set(diveId, result)
          return result
        }
      }

      const rows: TransactionRow[] = await Promise.all(
        rawTransactions.map(async tx => {
          const diveResult = await resolveDiveName(tx.diveId)
          return {
            ...tx,
            diveName: diveResult.name,
            diveStatus: diveResult.status,
            participantName: await resolveUserName(tx.payerId),
            hostName: await resolveUserName(tx.hostId)
          }
        })
      )

      setTransactions(rows)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load transactions'
      )
    } finally {
      setLoading(false)
    }
  }, [
    hostIdFilter,
    diveIdFilter,
    payoutStatusFilter,
    startDate,
    endDate,
    showTestData
  ])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  // Filter by completeness (stripeFee not null = completed)
  const filteredTransactions = useMemo(() => {
    if (showIncomplete) return transactions
    return transactions.filter(tx => tx.stripeFee !== null)
  }, [transactions, showIncomplete])

  // Summary calculations - only count completed transactions (stripeFee not null)
  const summary = useMemo(() => {
    const completed = transactions.filter(tx => tx.stripeFee !== null)

    const totalPending = completed
      .filter(tx => tx.payoutStatus === 'pending')
      .reduce((sum, tx) => sum + (tx.netOwedToHost ?? 0), 0)

    const totalProcessed = completed
      .filter(tx => tx.payoutStatus === 'processed')
      .reduce((sum, tx) => sum + (tx.netOwedToHost ?? 0), 0)

    const totalCredited = completed
      .filter(tx => tx.payoutStatus === 'credited')
      .reduce((sum, tx) => sum + (tx.netOwedToHost ?? 0), 0)

    const totalPlatformRevenue = completed.reduce(
      (sum, tx) => sum + (tx.platformCommission ?? 0),
      0
    )

    return { totalPending, totalProcessed, totalCredited, totalPlatformRevenue }
  }, [transactions])

  // Per-host total owed when filtered by host
  const perHostOwed = useMemo(() => {
    if (!hostIdFilter.trim()) return null
    const completed = transactions.filter(
      tx => tx.stripeFee !== null && tx.payoutStatus === 'pending'
    )
    return completed.reduce((sum, tx) => sum + (tx.netOwedToHost ?? 0), 0)
  }, [transactions, hostIdFilter])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 2
        }}
      >
        <Typography variant='h6'>Transaction Ledger</Typography>
        <Typography variant='h6' color='success.main'>
          Platform Revenue: {formatAUD(summary.totalPlatformRevenue)}
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label='Start Date'
          type='date'
          size='small'
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label='End Date'
          type='date'
          size='small'
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label='Host ID'
          size='small'
          value={hostIdFilter}
          onChange={e => setHostIdFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label='Dive ID'
          size='small'
          value={diveIdFilter}
          onChange={e => setDiveIdFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='payout-status-filter-label'>Payout Status</InputLabel>
          <Select
            labelId='payout-status-filter-label'
            value={payoutStatusFilter}
            label='Payout Status'
            onChange={e => setPayoutStatusFilter(e.target.value)}
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='pending'>Pending</MenuItem>
            <MenuItem value='processed'>Processed</MenuItem>
            <MenuItem value='credited'>Credited</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={showIncomplete}
              onChange={e => setShowIncomplete(e.target.checked)}
            />
          }
          label='Show incomplete transactions'
        />
      </Box>

      {/* Summary Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant='subtitle1' gutterBottom>
          Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant='body2' color='text.secondary'>
              Total Outstanding (Pending)
            </Typography>
            <Typography variant='h6'>
              {formatAUD(summary.totalPending)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' color='text.secondary'>
              Total Processed
            </Typography>
            <Typography variant='h6'>
              {formatAUD(summary.totalProcessed)}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' color='text.secondary'>
              Total Credited
            </Typography>
            <Typography variant='h6'>
              {formatAUD(summary.totalCredited)}
            </Typography>
          </Box>
          {perHostOwed !== null && (
            <Box>
              <Typography variant='body2' color='text.secondary'>
                Per-Host Total Owed
              </Typography>
              <Typography variant='h6'>{formatAUD(perHostOwed)}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Loading / Error */}
      {loading && (
        <Box display='flex' justifyContent='center' py={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Dive</TableCell>
                <TableCell>Dive Status</TableCell>
                <TableCell>Participant</TableCell>
                <TableCell>Host</TableCell>
                <TableCell align='right'>Amount (AUD)</TableCell>
                <TableCell align='right'>Stripe Fee</TableCell>
                <TableCell align='right'>Platform Commission</TableCell>
                <TableCell align='right'>Net Owed to Host</TableCell>
                <TableCell>Payout Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align='center'>
                    <Typography color='text.secondary' sx={{ py: 2 }}>
                      No transactions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map(tx => (
                  <TableRow key={tx.id}>
                    <TableCell>
                      <Link
                        component='button'
                        variant='body2'
                        onClick={() => handleDiveClick(tx)}
                        sx={{ cursor: 'pointer', textAlign: 'left' }}
                      >
                        {tx.diveName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {tx.diveStatus ? (
                        <Chip
                          label={tx.diveStatus}
                          color={DIVE_STATUS_COLORS[tx.diveStatus] ?? 'default'}
                          size='small'
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{tx.participantName}</TableCell>
                    <TableCell>{tx.hostName}</TableCell>
                    <TableCell align='right'>{formatAUD(tx.amount)}</TableCell>
                    <TableCell align='right'>
                      {formatAUD(tx.stripeFee)}
                    </TableCell>
                    <TableCell align='right'>
                      {formatAUD(tx.platformCommission)}
                    </TableCell>
                    <TableCell align='right'>
                      {formatAUD(tx.netOwedToHost)}
                    </TableCell>
                    <TableCell>{tx.payoutStatus}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dive Detail Modal */}
      <Dialog
        open={diveDetailOpen}
        onClose={() => {
          setDiveDetailOpen(false)
          setSelectedDiveDetail(null)
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Dive Details</DialogTitle>
        <DialogContent>
          {diveDetailLoading && (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          )}
          {!diveDetailLoading && !selectedDiveDetail && (
            <Typography color='text.secondary'>
              Dive details not available.
            </Typography>
          )}
          {!diveDetailLoading && selectedDiveDetail && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                pt: 1
              }}
            >
              <Typography variant='body2'>
                <strong>Title:</strong> {selectedDiveDetail.siteName}
              </Typography>
              <Typography variant='body2'>
                <strong>Host:</strong> {selectedDiveHostName}
              </Typography>
              <Typography variant='body2'>
                <strong>Description:</strong> {selectedDiveDetail.description}
              </Typography>
              <Typography variant='body2'>
                <strong>Location:</strong> {selectedDiveDetail.locationDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Date:</strong> {selectedDiveDetail.dateTimeDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Price:</strong>{' '}
                {selectedDiveDetail.price != null
                  ? formatAUD(selectedDiveDetail.price)
                  : 'Free'}
              </Typography>
              <Typography variant='body2'>
                <strong>Capacity:</strong> {selectedDiveDetail.maxParticipants}
              </Typography>
              <Typography variant='body2'>
                <strong>Type:</strong> {selectedDiveDetail.type}
              </Typography>
              <Typography variant='body2'>
                <strong>Duration:</strong> {selectedDiveDetail.duration} min
              </Typography>
              <Typography variant='body2'>
                <strong>Depth Tier:</strong> {selectedDiveDetail.depthTier}
              </Typography>
              <Typography variant='body2'>
                <strong>Status:</strong>{' '}
                <Chip
                  label={selectedDiveDetail.status}
                  color={
                    DIVE_STATUS_COLORS[selectedDiveDetail.status] ?? 'default'
                  }
                  size='small'
                />
              </Typography>
              {selectedDiveDetail.status === 'removed' &&
                selectedDiveDetail.moderationReason && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Alert severity='warning' sx={{ mt: 1 }}>
                      <strong>Removal reason:</strong>{' '}
                      {selectedDiveDetail.moderationReason}
                    </Alert>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiveDetailOpen(false)
              setSelectedDiveDetail(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TransactionLedgerTab
